(() => {
  "use strict";

  const root = document.getElementById("ikarao-support-v4");
  if (!root) return;

  const pageKey = root.dataset.supportPage || "";
  const DEFAULT_ROUTES = {
    home: "/pages/support",
    guide: "/pages/support-guided-help",
    product: "/pages/support-product",
    identify: "/pages/support-device-info",
    video: "/pages/support-video-guide",
    manuals: "/pages/support-manuals",
    contact: "/pages/support-contact",
    tracking: "/pages/support-track-order",
    policy: "/pages/support-policy",
    faqLegacy: "/pages/support-faqs",
    progressLegacy: "/pages/support-progress",
    repairLegacy: "/pages/support-repair",
    officialForm: "/pages/support-backup-20260911"
  };
  const configuredRoutes = window.IKARAO_SUPPORT_ROUTES;
  const routeOverrides = configuredRoutes && typeof configuredRoutes === "object"
    ? configuredRoutes
    : {};
  const routes = Object.fromEntries(Object.entries(DEFAULT_ROUTES).map(([key, fallback]) => [
    key,
    typeof routeOverrides[key] === "string" && routeOverrides[key].trim()
      ? routeOverrides[key].trim()
      : fallback
  ]));

  const routeUrl = (value) => new URL(
    typeof value === "string" && value ? value : routes.home,
    window.location.origin
  );

  const currentParams = new URLSearchParams(window.location.search);
  const copyPreviewContext = (target) => {
    ["view", "preview_theme_id"].forEach((name) => {
      const value = currentParams.get(name);
      if (value) target.searchParams.set(name, value);
    });
    return target;
  };

  const preservePreviewContextOnLinks = () => {
    if (!currentParams.get("view") && !currentParams.get("preview_theme_id")) return;
    const routePaths = new Set(Object.values(routes).map((value) => {
      try { return routeUrl(value).pathname; }
      catch { return ""; }
    }).filter(Boolean));

    root.querySelectorAll("a[href]").forEach((link) => {
      let target;
      try { target = new URL(link.getAttribute("href"), window.location.href); }
      catch { return; }
      if (target.origin !== window.location.origin || !routePaths.has(target.pathname)) return;
      copyPreviewContext(target);
      link.setAttribute("href", `${target.pathname}${target.search}${target.hash}`);
    });
  };

  const FAQ_TARGETS = {
    "#faq-01": "?flow=karafun&branch=offer#help-activation",
    "#faq-02": "?flow=setup&branch=first#help-x1-guide",
    "#faq-03": "?flow=setup&branch=first#help-x2-guide",
    "#faq-04": "?flow=apps&branch=update#help-version",
    "#faq-05": "?flow=sound&branch=voice#help-mic-support",
    "#faq-06": "?flow=sound&branch=charging#help-mic-charge",
    "#faq-07": "?flow=apps&branch=wifi#help-wifi",
    "#faq-08": "?flow=setup&branch=tv#help-hdmi",
    "#faq-09": "?flow=setup&branch=choose#help-compare",
    "#faq-10": "?flow=setup&branch=accessories#help-accessory",
    "#faq-11": "?flow=sound&branch=voice#help-mic-support",
    "#faq-12": "?flow=apps&branch=update#help-updates",
    "#faq-13": "?flow=setup&branch=first#help-box",
    "#faq-14": "?flow=setup&branch=first#help-overview",
    "#faq-15": "?flow=setup&branch=first#help-simple-start",
    "#faq-16": "?flow=setup&branch=first#help-screen",
    "#faq-17": "?flow=power&branch=unplugged#help-portable",
    "#faq-18": "?flow=power&branch=unplugged#help-portable",
    "#faq-19": "?flow=sound&branch=phone#help-phone",
    "#faq-20": "?flow=setup&branch=outdoor#help-outdoor",
    "#faq-21": "?flow=sound&branch=charging#help-mic-charge",
    "#faq-22": "?flow=orders&branch=delivery#help-destination",
    "#faq-23": "?flow=orders&branch=costs#help-charges",
    "#faq-24": "?flow=orders&branch=delivery#help-delivery",
    "#faq-25": "?flow=orders&branch=address#help-address",
    "#faq-26": "?flow=orders&branch=costs#help-charges",
    "#faq-27": "?flow=orders&branch=damage#help-damage",
    "#faq-28": "?flow=orders&branch=address#help-address",
    "#faq-29": "?flow=orders&branch=tracking#help-tracking",
    "#faq-30": "?flow=orders&branch=tracking#help-carrier",
    "#faq-31": "?flow=orders&branch=delivery#help-split",
    "#faq-32": "?flow=service&branch=warranty#help-warranty",
    "#faq-33": "?flow=service&branch=warranty#help-warranty",
    "#faq-34": "?flow=service&branch=request#help-problem",
    "#faq-35": "?flow=service&branch=request#help-contact",
    "#faq-36": "?flow=service&branch=exchange#help-exchange",
    "#faq-37": "?flow=setup&branch=accessories#help-accessory",
    "#faq-38": "?flow=power&branch=replacement#help-charger"
  };

  const redirectLegacyPage = () => {
    if (pageKey === "faqLegacy") {
      const hash = window.location.hash.toLowerCase();
      const savedTarget = FAQ_TARGETS[hash];
      const target = routeUrl(routes.guide);

      if (savedTarget) {
        const parsed = new URL(savedTarget, window.location.origin);
        target.search = parsed.search;
        target.hash = parsed.hash;
      } else {
        const oldParams = new URLSearchParams(window.location.search);
        const term = oldParams.get("q") || oldParams.get("search");
        if (term) target.searchParams.set("search", term);
        else if (hash === "#shipping") target.searchParams.set("flow", "orders");
        else if (hash === "#service") target.searchParams.set("flow", "service");
        else if (hash === "#product") target.searchParams.set("flow", "setup");
      }

      copyPreviewContext(target);
      window.location.replace(target.href);
      return true;
    }

    if (pageKey === "progressLegacy") {
      const target = routeUrl(routes.contact);
      target.hash = "existing-request";
      copyPreviewContext(target);
      window.location.replace(target.href);
      return true;
    }

    if (pageKey === "repairLegacy") {
      const target = routeUrl(routes.contact);
      const source = new URLSearchParams(window.location.search);
      const issue = source.get("issue");
      const model = source.get("model");
      const supportedIssues = new Set(["shipping", "hdmi", "app", "indicator"]);

      if (issue && supportedIssues.has(issue)) {
        if (model) target.searchParams.set("model", model);
        target.searchParams.set("issue", issue);
      }
      target.hash = "request";
      window.location.replace(target.href);
      return true;
    }

    return false;
  };

  if (redirectLegacyPage()) return;
  preservePreviewContextOnLinks();

  const VIDEO_GROUPS = [
    ["01", "Product introduction", ["guide-quick-start", "guide-hardware", "guide-software"]],
    ["02", "How to sing", ["guide-karafun", "guide-youtube-karaoke", "guide-local-files", "guide-microphone-setup", "guide-mobile-apps"]],
    ["03", "How to listen", ["guide-spotify", "guide-apple-music", "guide-external-audio-in"]],
    ["04", "How to watch", ["guide-youtube-video", "guide-tv-projector"]],
    ["05", "How to play", ["guide-electric-guitar", "guide-external-audio-out"]],
    ["06", "Other useful features", ["guide-charging", "guide-dsp-effects", "guide-firmware-update", "guide-volume"]]
  ];

  const VIDEOS = [
    ["guide-quick-start", "How do I start singing with IKARAO?", "hiWcIQOMcpY"],
    ["guide-hardware", "Where are the buttons and ports on my IKARAO?", "ycEsTHpZmH0"],
    ["guide-software", "How do I use the IKARAO touchscreen and software?", "-_vbbt33PGo"],
    ["guide-karafun", "How do I sing with KaraFun and use the 6-month pass?", "s6rSN3ZnyOc"],
    ["guide-youtube-karaoke", "How do I sing karaoke with YouTube?", "WgRIJ97utzo"],
    ["guide-local-files", "How do I play karaoke files from USB or a TF card?", "wQAuEqLtHzc"],
    ["guide-microphone-setup", "How do I set up and sync the wireless microphones?", "pP-wZr_hZn8"],
    ["guide-mobile-apps", "How do I connect mobile apps to IKARAO?", "z9DTX_Y1lCo"],
    ["guide-spotify", "How do I stream Spotify on IKARAO?", "dmswzssoSNM"],
    ["guide-apple-music", "How do I stream Apple Music on IKARAO?", "YSKznkCnxkQ"],
    ["guide-external-audio-in", "How do I play audio from an external device?", "xk4k-08rrVg"],
    ["guide-youtube-video", "How do I watch YouTube videos on IKARAO?", "Gaxb8mQ_ZY4"],
    ["guide-tv-projector", "How do I connect IKARAO to a TV or projector?", "Famwsupi8GE"],
    ["guide-electric-guitar", "How do I connect and use an electric guitar?", "XAGSJQEv3Gc"],
    ["guide-external-audio-out", "How do I output IKARAO sound to an external speaker?", "KTwbbwSLCb8"],
    ["guide-charging", "How do I charge the machine and microphones correctly?", "9HN98qWvrPI"],
    ["guide-dsp-effects", "How do I adjust microphone DSP sound effects?", "cO6m-xqvD3M"],
    ["guide-firmware-update", "How do I update IKARAO firmware over the air?", "d1f4j-pIgYg"],
    ["guide-volume", "How do I adjust master, music and microphone volume?", "aWWLT-FZQJ0"]
  ].map(([id, title, videoId]) => ({ id, title, videoId }));

  const initVideoGuide = () => {
    if (pageKey !== "video") return;

    let player = root.querySelector("[data-guide-player]");
    const title = root.querySelector("[data-guide-title]");
    const directory = root.querySelector("[data-guide-directory]");
    if (!player || !directory) return;

    const byId = new Map(VIDEOS.map((item) => [item.id, item]));

    VIDEO_GROUPS.forEach(([number, groupTitle, ids]) => {
      const card = document.createElement("article");
      card.className = "tutorial-category-card";

      const heading = document.createElement("div");
      heading.className = "category-heading";
      const badge = document.createElement("span");
      badge.className = "category-number";
      badge.textContent = number;
      const headingText = document.createElement("h3");
      headingText.textContent = groupTitle;
      heading.append(badge, headingText);

      const list = document.createElement("div");
      list.className = "guide-directory-list";
      ids.map((id) => byId.get(id)).filter(Boolean).forEach((item) => {
        const button = document.createElement("button");
        button.className = "guide-directory-button";
        button.type = "button";
        button.setAttribute("aria-pressed", "false");
        button.dataset.guideVideo = item.videoId;
        button.dataset.guideTitleValue = item.title;
        const label = document.createElement("span");
        label.textContent = item.title;
        const arrow = document.createElement("span");
        arrow.setAttribute("aria-hidden", "true");
        arrow.textContent = "→";
        button.append(label, arrow);
        list.append(button);
      });

      card.append(heading, list);
      directory.append(card);
    });

    const buttons = Array.from(root.querySelectorAll("[data-guide-video]"));
    const findVideo = (videoId) => VIDEOS.find((item) => item.videoId === videoId);
    const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const showVideo = (item, updateUrl, scroll) => {
      if (!item) return;
      const replacement = player.cloneNode(false);
      replacement.src = `https://www.youtube.com/embed/${encodeURIComponent(item.videoId)}?rel=0`;
      replacement.title = item.title;
      player.replaceWith(replacement);
      player = replacement;
      if (title) title.textContent = item.title;

      buttons.forEach((button) => {
        const selected = button.dataset.guideVideo === item.videoId;
        button.classList.toggle("active", selected);
        button.setAttribute("aria-pressed", selected ? "true" : "false");
      });

      if (updateUrl) {
        const next = new URL(window.location.href);
        next.hash = `video-${item.videoId}`;
        window.history.replaceState({ videoId: item.videoId }, "", next);
      }

      if (scroll) {
        root.querySelector(".guide-video-column")?.scrollIntoView({
          behavior: reducedMotion() ? "auto" : "smooth",
          block: "start"
        });
      }
    };

    buttons.forEach((button) => {
      button.addEventListener("click", () => {
        showVideo(findVideo(button.dataset.guideVideo), true, button.classList.contains("guide-directory-button"));
      });
    });

    const renderFromHash = () => {
      const requestedId = window.location.hash.startsWith("#video-")
        ? window.location.hash.slice(7)
        : "";
      showVideo(findVideo(requestedId) || VIDEOS[0], false, Boolean(requestedId));
    };

    window.addEventListener("hashchange", renderFromHash);
    renderFromHash();
  };

  const initPolicyNavigation = () => {
    if (pageKey !== "policy") return;
    const nav = root.querySelector(".side-panel nav");
    if (!nav) return;

    const links = Array.from(nav.querySelectorAll("a[href^='#']"));
    const targets = links.map((link) => {
      const id = link.getAttribute("href").slice(1);
      const target = document.getElementById(id);
      return target && root.contains(target) ? { link, target } : null;
    }).filter(Boolean);
    if (!targets.length) return;

    const setActive = (selected) => {
      links.forEach((link) => {
        const active = link === selected;
        link.classList.toggle("active", active);
        if (active) link.setAttribute("aria-current", "location");
        else link.removeAttribute("aria-current");
      });
    };

    const updateFromHash = () => {
      const selected = targets.find(({ link }) => link.hash === window.location.hash);
      setActive(selected ? selected.link : targets[0].link);
    };

    let scrollFrame = 0;
    const updateFromScroll = () => {
      scrollFrame = 0;
      const offset = Number.parseFloat(getComputedStyle(root).getPropertyValue("--support-anchor-offset")) || 72;
      const marker = Math.min(window.innerHeight * 0.33, offset + 160);
      let selected = targets[0].link;
      targets.forEach((item) => {
        if (item.target.getBoundingClientRect().top <= marker) selected = item.link;
      });
      setActive(selected);
    };

    links.forEach((link) => link.addEventListener("click", () => setActive(link)));
    window.addEventListener("hashchange", updateFromHash);
    window.addEventListener("scroll", () => {
      if (!scrollFrame) scrollFrame = window.requestAnimationFrame(updateFromScroll);
    }, { passive: true });
    updateFromHash();
    window.requestAnimationFrame(updateFromScroll);
  };

  initVideoGuide();
  initPolicyNavigation();
})();
